import { motion } from "framer-motion";
import { useState } from "react";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

interface RefreshButtonProps {
  onClick?: () => void;
  isRefreshing?: boolean;
  disabled?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  hasNewData?: boolean;
  tooltip?: string;
}

const RefreshIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

const RefreshButton = ({
  onClick,
  isRefreshing = false,
  disabled = false,
  size = "md",
  className,
  hasNewData = false,
  tooltip,
}: RefreshButtonProps) => {
  const [triggerRotation, setTriggerRotation] = useState(0);

  const sizeClasses = {
    xs: "h-6 w-6",
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
  };

  const iconSizes = {
    xs: 14,
    sm: 16,
    md: 18,
    lg: 20,
  };
  const dotSizes = {
    xs: "h-1.5 w-1.5",
    sm: "h-2 w-2",
    md: "h-2 w-2",
    lg: "h-2.5 w-2.5",
  };

  const dotPositions = {
    xs: "top-0 right-0",
    sm: "top-0 right-0",
    md: "top-0 right-0",
    lg: "top-0 right-0",
  };

  const handleClick = () => {
    setTriggerRotation(prev => prev + 360);
    onClick?.();
  };

  const button = (
    <>
      <motion.button
        onClick={handleClick}
        disabled={disabled || isRefreshing}
        className={cn(
          "inline-flex items-center justify-center",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          "transition-all duration-200 ease-in-out",
          isRefreshing ? "text-blue-500" : "text-gray-400",
          hasNewData && !isRefreshing && "text-blue-600 hover:text-blue-700",
          sizeClasses[size],
          className,
        )}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: "spring", stiffness: 500, damping: 25 }}
      >
        <motion.div
          animate={isRefreshing ? { rotate: triggerRotation + 360 } : { rotate: triggerRotation }}
          transition={
            isRefreshing
              ? {
                  duration: 0.8,
                  repeat: Infinity,
                  ease: "linear",
                }
              : {
                  duration: 0.3,
                  ease: "easeOut",
                }
          }
        >
          <RefreshIcon size={iconSizes[size]} />
        </motion.div>
      </motion.button>
      {hasNewData && !isRefreshing && (
        <motion.span
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 25,
            duration: 0.2,
          }}
          className={cn(
            "absolute rounded-full bg-blue-500 shadow-sm ring-2 ring-white",
            dotSizes[size],
            dotPositions[size],
          )}
          aria-hidden="true"
        >
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="h-full w-full rounded-full bg-blue-500"
          />
        </motion.span>
      )}
    </>
  );

  return (
    <div className="relative">
      {tooltip ? (
        <BasicTooltip content={tooltip}>
          <span className="inline-flex">{button}</span>
        </BasicTooltip>
      ) : (
        button
      )}
    </div>
  );
};

export default RefreshButton;
